import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AwsCostosService, CostSummaryResponse, MonthlyCost, DailyCost, ServiceCost, AIAnalysis, MarketplaceCost } from '../../services/aws-costos/aws-costos.service';
import { LoadingService } from '../../services/general/loading.service';
import { PreloaderComponent } from '../shared/preloader/preloader.component';
import ApexCharts from 'apexcharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

@Component({
  selector: 'app-aws-costos',
  standalone: true,
  imports: [CommonModule, PreloaderComponent, FormsModule],
  templateUrl: './aws-costos.component.html',
  styleUrl: './aws-costos.component.css'
})
export class AwsCostosComponent implements OnInit {
  @ViewChild('contentWrapper') contentWrapper!: ElementRef;
  public barChart: ApexCharts | null = null;
  public comparisonChart: ApexCharts | null = null;
  public Math = Math;
  public isExporting: boolean = false;
  public showKpiLine: boolean = true;

  mesReferencia: string = '';
  mesComparacion: string = '';
  monthOptions: { value: string; label: string }[] = [];
  kpiMensual: number = 26000;
  
  costData: any = null;
  metadata: any = null;
  aiAnalysis: AIAnalysis | null = null;
  costsByServiceCurrent: ServiceCost[] = [];
  costsByServicePrevious: ServiceCost[] = [];
  errorMessage: string = '';
  hasData: boolean = false;
  serviciosAnalisis: { [servicioNombre: string]: string } = {};

  // Marketplace
  marketplaceCostReferencia: MarketplaceCost | null = null;
  marketplaceCostComparacion: MarketplaceCost | null = null;
  includeMarketplace: boolean = true;

  constructor(
    private readonly awsCostosService: AwsCostosService,
    public readonly loadingService: LoadingService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.generateMonthOptions();
    this.setDefaultMonths();
  }

  generateMonthOptions(): void {
    const now = new Date();
    const months: { value: string; label: string }[] = [];
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    // Generate last 24 months
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      months.push({ value, label });
    }
    this.monthOptions = months;
  }

  setDefaultMonths(): void {
    const now = new Date();
    this.mesReferencia = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    this.updateMesComparacion();
  }

  onMesReferenciaChange(): void {
    this.updateMesComparacion();
  }

  private updateMesComparacion(): void {
    const [year, month] = this.mesReferencia.split('-').map(Number);
    const prev = new Date(year, month - 2, 1);
    this.mesComparacion = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  }

  get mesReferenciaLabel(): string {
    return this.monthOptions.find(o => o.value === this.mesReferencia)?.label || this.mesReferencia;
  }

  get mesComparacionLabel(): string {
    return this.monthOptions.find(o => o.value === this.mesComparacion)?.label || this.mesComparacion;
  }

  get refYear(): string {
    return this.mesReferencia.split('-')[0];
  }

  get kpiCostoUltimoMes(): number {
    if (!this.costData) return 0;
    const yearKey = `Total ${this.refYear}`;
    const data: MonthlyCost[] = this.costData[yearKey];
    if (!data || data.length === 0) return 0;
    return data[data.length - 1].costo;
  }

  get kpiPorcentaje(): number {
    if (this.kpiMensual <= 0) return 0;
    return (this.kpiCostoUltimoMes / this.kpiMensual) * 100;
  }

  get kpiCumple(): boolean {
    return this.kpiCostoUltimoMes <= this.kpiMensual;
  }

  get kpiUltimoMesLabel(): string {
    if (!this.costData) return '';
    const yearKey = `Total ${this.refYear}`;
    const data: MonthlyCost[] = this.costData[yearKey];
    if (!data || data.length === 0) return '';
    return data[data.length - 1].mes;
  }

  // ===== Marketplace =====

  get hasMarketplaceData(): boolean {
    return !!(this.marketplaceCostReferencia || this.marketplaceCostComparacion);
  }

  /** Costo de marketplace del mes de referencia */
  get marketplaceRefCost(): number {
    return this.marketplaceCostReferencia?.cost || 0;
  }

  /** Costo de marketplace del mes de comparación */
  get marketplaceCompCost(): number {
    return this.marketplaceCostComparacion?.cost || 0;
  }

  /** Total del mes de referencia, ajustado según el toggle de marketplace */
  get refMonthTotal(): number {
    const base = this.costData?.monthly_comparison?.current_month?.total || 0;
    return this.includeMarketplace ? base : base - this.marketplaceRefCost;
  }

  /** Total del mes de comparación, ajustado según el toggle de marketplace */
  get compMonthTotal(): number {
    const base = this.costData?.monthly_comparison?.previous_month?.total || 0;
    return this.includeMarketplace ? base : base - this.marketplaceCompCost;
  }

  /** Diferencia (referencia - comparación) recalculada con el ajuste de marketplace */
  get monthDifference(): number {
    return this.refMonthTotal - this.compMonthTotal;
  }

  /** Variación porcentual recalculada con el ajuste de marketplace */
  get monthPercentageChange(): number {
    if (this.compMonthTotal === 0) return 0;
    return (this.monthDifference / this.compMonthTotal) * 100;
  }

  // Resumen ejecutivo ajustado según el toggle de marketplace
  get resumenCostoReciente(): number {
    const base = this.aiAnalysis?.resumen_ejecutivo?.costo_mes_reciente || 0;
    return this.includeMarketplace ? base : base - this.marketplaceRefCost;
  }

  get resumenCostoComparacion(): number {
    const base = this.aiAnalysis?.resumen_ejecutivo?.costo_mes_comparacion || 0;
    return this.includeMarketplace ? base : base - this.marketplaceCompCost;
  }

  get resumenDiferencia(): number {
    return this.resumenCostoReciente - this.resumenCostoComparacion;
  }

  get resumenPorcentaje(): number {
    if (this.resumenCostoComparacion === 0) return 0;
    return (this.resumenDiferencia / this.resumenCostoComparacion) * 100;
  }

  loadCostData(): void {
    this.loadingService.setLoading(true);
    const token = localStorage.getItem('authToken') || '';

    this.awsCostosService.getCostsSummary(token, this.mesReferencia, this.mesComparacion).subscribe({
      next: (response: CostSummaryResponse) => {
        if (response.success && response.data) {
          this.costData = response.data;
          this.metadata = response.metadata;
          this.aiAnalysis = response.ai_analysis || null;
          this.costsByServiceCurrent = response.costs_by_service_current || [];
          this.costsByServicePrevious = response.costs_by_service_previous || [];
          this.marketplaceCostReferencia = response.marketplace_cost_referencia || null;
          this.marketplaceCostComparacion = response.marketplace_cost_comparacion || null;
          this.hasData = true;
          setTimeout(() => this.renderCharts(), 100);
        }
      },
      error: (error) => {
        this.loadingService.setLoading(false);
        console.error('Error al cargar datos de costos:', error);
        
        // Detectar errores de límite de peticiones
        const errorMsg = error?.message?.toLowerCase() || '';
        const errorStatus = error?.status;
        
        if (errorStatus === 429 || errorMsg.includes('limit') || errorMsg.includes('rate') || errorMsg.includes('quota') || errorMsg.includes('too many')) {
          this.errorMessage = 'Se ha alcanzado el límite de peticiones. Por favor, espera unos momentos antes de intentar nuevamente.';
        } else if (errorStatus === 401 || errorStatus === 403) {
          this.errorMessage = 'No tienes permisos para acceder a esta información. Por favor, verifica tu sesión.';
        } else if (errorStatus === 500 || errorStatus === 502 || errorStatus === 503) {
          this.errorMessage = 'Error en el servidor. Por favor, intenta nuevamente en unos minutos.';
        } else {
          this.errorMessage = error?.message || 'Error al cargar los datos de costos. Por favor, intenta nuevamente.';
        }
        
        this.hasData = false;
      },
      complete: () => {
        this.loadingService.setLoading(false);
      }
    });
  }

  renderCharts(): void {
    if (!this.costData) return;

    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    
    // Preparar datos para gráficos
    const barSeries: any[] = [];
    const years = ['Total 2024', 'Total 2025', 'Total 2026'];
    
    years.forEach(year => {
      if (this.costData[year] && this.costData[year].length > 0) {
        barSeries.push({
          name: year,
          data: this.costData[year].map((item: MonthlyCost) => item.costo)
        });
      }
    });

    // Obtener categorías (meses) del año con más datos
    const maxDataYear = years.reduce((prev, curr) => {
      return (this.costData[curr]?.length || 0) > (this.costData[prev]?.length || 0) ? curr : prev;
    }, years[0]);

    const categories = this.costData[maxDataYear]?.map((item: MonthlyCost) => item.mes) || months;

    // Calcular rango del eje Y para asegurar que el KPI sea visible
    let allValues: number[] = [];
    barSeries.forEach(series => {
      allValues = allValues.concat(series.data);
    });
    
    const maxDataValue = allValues.length > 0 ? Math.max(...allValues) : 0;
    
    // Si hay KPI configurado, asegurarse de que el eje Y lo incluya
    let yAxisMax: number | undefined = undefined;
    let yAxisMin: number | undefined = undefined;
    
    if (this.kpiMensual > 0) {
      // Extender el rango para incluir el KPI con un margen
      yAxisMax = Math.max(maxDataValue, this.kpiMensual) * 1.15;
      yAxisMin = 0;
    }

    // Configuración para gráfico de barras
    const barOptions: any = {
      series: barSeries,
      chart: {
        type: 'bar' as const,
        height: 450,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          }
        },
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '70%',
          borderRadius: 8,
          dataLabels: {
            position: 'top' as const
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500
          }
        }
      },
      yaxis: {
        min: yAxisMin,
        max: yAxisMax,
        title: {
          text: 'Costo (USD)',
          style: {
            fontSize: '14px',
            fontWeight: 600
          }
        },
        labels: {
          formatter: (val: number) => {
            return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
          }
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        y: {
          formatter: (val: number) => {
            return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
        }
      },
      colors: ['#10b981', '#3b82f6', '#f59e0b'],
      legend: {
        position: 'top' as const,
        horizontalAlign: 'right' as const,
        fontSize: '14px',
        fontWeight: 500
      },
      grid: {
        borderColor: '#e5e7eb',
        strokeDashArray: 4
      },
      annotations: {
        yaxis: this.kpiMensual > 0 ? [{
          y: this.kpiMensual,
          borderColor: '#ef4444',
          strokeDashArray: 4,
          label: {
            borderColor: '#ef4444',
            style: {
              color: '#fff',
              background: '#ef4444',
              fontSize: '12px',
              fontWeight: 600,
              padding: { left: 8, right: 8, top: 4, bottom: 4 }
            },
            text: `KPI $${this.kpiMensual.toLocaleString('en-US')}`,
            position: 'left' as const
          }
        }] : []
      }
    };

    // Configuración para gráfico de comparación mensual
    const monthlyComparison = this.costData.monthly_comparison;
    if (monthlyComparison) {
      const currentMonthData = monthlyComparison.current_month.daily_costs.map((d: DailyCost) => d.cost);
      const previousMonthData = monthlyComparison.previous_month.daily_costs.map((d: DailyCost) => d.cost);
      const daysCategories = monthlyComparison.current_month.daily_costs.map((d: DailyCost) => d.day.toString());

      // Calcular min/max para escala óptima del eje Y
      const allData = [...currentMonthData, ...previousMonthData];
      const minValue = Math.min(...allData);
      const maxValue = Math.max(...allData);
      const padding = (maxValue - minValue) * 0.15;
      const yMin = Math.max(0, minValue - padding);
      const yMax = maxValue + padding;

      const comparisonOptions = {
        series: [
          {
            name: `${monthlyComparison.current_month.month_name} ${monthlyComparison.current_month.year}`,
            data: currentMonthData
          },
          {
            name: `${monthlyComparison.previous_month.month_name} ${monthlyComparison.previous_month.year}`,
            data: previousMonthData
          }
        ],
        chart: {
          type: 'line' as const,
          height: 420,
          toolbar: {
            show: true,
            offsetY: -5,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          zoom: {
            enabled: false
          },
          pan: {
            enabled: false
          },
          selection: {
            enabled: false
          },
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 600
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth' as const,
          width: [3, 3],
          dashArray: [0, 0]
        },
        markers: {
          size: [4, 4],
          strokeWidth: 2,
          strokeColors: ['#fff', '#fff'],
          hover: {
            size: 6
          }
        },
        xaxis: {
          categories: daysCategories,
          title: {
            text: 'Día del mes',
            style: {
              fontSize: '12px',
              fontWeight: 600
            },
            offsetY: 5
          },
          labels: {
            style: {
              fontSize: '11px',
              fontWeight: 500
            },
            rotate: 0
          },
          tickAmount: 15,
          axisBorder: {
            show: true,
            color: '#e5e7eb'
          },
          axisTicks: {
            show: true,
            color: '#e5e7eb'
          }
        },
        yaxis: {
          min: yMin,
          max: yMax,
          tickAmount: 6,
          title: {
            text: 'Costo (USD)',
            style: {
              fontSize: '12px',
              fontWeight: 600
            }
          },
          labels: {
            formatter: (val: number) => {
              return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
            },
            style: {
              fontSize: '11px'
            }
          }
        },
        tooltip: {
          shared: true,
          intersect: false,
          x: {
            formatter: (val: number) => `Día ${val}`
          },
          y: {
            formatter: (val: number) => {
              return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
          }
        },
        colors: ['#10b981', '#6366f1'],
        legend: {
          position: 'top' as const,
          horizontalAlign: 'center' as const,
          fontSize: '13px',
          fontWeight: 500,
          offsetY: 0,
          markers: {
            width: 12,
            height: 12,
            radius: 3
          },
          itemMargin: {
            horizontal: 15
          }
        },
        grid: {
          borderColor: '#f3f4f6',
          strokeDashArray: 3,
          padding: {
            top: 10,
            right: 20,
            bottom: 0,
            left: 10
          }
        }
      };

      const comparisonChartElement = document.querySelector('#comparisonChart');
      if (comparisonChartElement) {
        if (this.comparisonChart) {
          this.comparisonChart.destroy();
        }
        this.comparisonChart = new ApexCharts(comparisonChartElement, comparisonOptions);
        this.comparisonChart.render();
      }
    }

    // Renderizar gráfico de barras
    const barChartElement = document.querySelector('#barChart');
    if (barChartElement) {
      if (this.barChart) {
        this.barChart.destroy();
      }
      this.barChart = new ApexCharts(barChartElement, barOptions);
      this.barChart.render();
    }
  }

  getTotalCostByYear(year: string): number {
    if (!this.costData?.hasOwnProperty(year)) return 0;
    return this.costData[year].reduce((sum: number, item: MonthlyCost) => sum + item.costo, 0);
  }

  getYearPercentage(year: string): number {
    const total = ['Total 2024', 'Total 2025', 'Total 2026']
      .reduce((sum, y) => sum + this.getTotalCostByYear(y), 0);
    if (total === 0) return 0;
    return (this.getTotalCostByYear(year) / total) * 100;
  }

  getYearColor(year: string): string {
    const colors: any = {
      'Total 2024': '#10b981',
      'Total 2025': '#3b82f6',
      'Total 2026': '#f59e0b'
    };
    return colors[year] || '#10b981';
  }

  getTipoBadgeText(tipo: string): string {
    const tipos: { [key: string]: string } = {
      'reduccion': 'Reducción',
      'aumento': 'Aumento',
      'normalizacion': 'Estable',
      'optimizacion': 'Optimización'
    };
    return tipos[tipo] || tipo;
  }

  /**
   * Limpia la descripción del servicio removiendo el monto duplicado
   * que ya aparece en el badge
   */
  limpiarDescripcion(descripcion: string, montoCambio: string): string {
    if (!descripcion || !montoCambio) return descripcion;
    
    // Remover el monto_cambio exacto si aparece en la descripción
    let cleaned = descripcion.replace(montoCambio, '').trim();
    
    // Remover patrones comunes de montos ($XXX.XX (XX.XX%))
    cleaned = cleaned.replaceAll(/\$[-+]?\d{1,3}(,\d{3})*(\.\d{2})?\s*\([-+]?\d+(\.\d{1,2})?%\)/g, '').trim();
    
    // Limpiar espacios múltiples y puntos duplicados
    cleaned = cleaned.replaceAll(/\s+/g, ' ').replaceAll(/\.\s*\./g, '.').trim();
    
    return cleaned;
  }

  toggleKpiLine(): void {
    if (!this.barChart) return;
    this.barChart.updateOptions({
      annotations: {
        yaxis: this.showKpiLine && this.kpiMensual > 0 ? [{
          y: this.kpiMensual,
          borderColor: '#ef4444',
          strokeDashArray: 4,
          label: {
            borderColor: '#ef4444',
            style: {
              color: '#fff',
              background: '#ef4444',
              fontSize: '12px',
              fontWeight: 600,
              padding: { left: 8, right: 8, top: 4, bottom: 4 }
            },
            text: `KPI $${this.kpiMensual.toLocaleString('en-US')}`,
            position: 'left'
          }
        }] : []
      }
    });
  }

  getServicePreviousCost(serviceName: string): number {
    const service = this.costsByServicePrevious.find(s => s.service === serviceName);
    return service?.cost || 0;
  }

  getServiceDifference(serviceName: string): number {
    const currentService = this.costsByServiceCurrent.find(s => s.service === serviceName);
    const previousService = this.costsByServicePrevious.find(s => s.service === serviceName);
    const currentCost = currentService?.cost || 0;
    const previousCost = previousService?.cost || 0;
    return currentCost - previousCost;
  }

  async exportToPDF(): Promise<void> {
    if (!this.contentWrapper || !this.hasData) return;

    this.isExporting = true;
    this.cdr.detectChanges();
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const element = this.contentWrapper.nativeElement;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#f5f7fa'
      });

      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageWidth = 297;
      const pageHeight = 210;
      const margin = 10;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const totalPages = Math.ceil(imgHeight / usableHeight);

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();

        const sourceY = page * (canvas.width * usableHeight / usableWidth);
        const sourceH = Math.min(canvas.width * usableHeight / usableWidth, canvas.height - sourceY);

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sourceH;
        const ctx = sliceCanvas.getContext('2d');
        if (!ctx) continue;
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceH, 0, 0, canvas.width, sourceH);

        const sliceData = sliceCanvas.toDataURL('image/png');
        const sliceImgHeight = (sourceH * imgWidth) / canvas.width;
        pdf.addImage(sliceData, 'PNG', margin, margin, imgWidth, sliceImgHeight);

        // Footer
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(`Costos AWS — ${this.mesReferenciaLabel} vs ${this.mesComparacionLabel}  |  Página ${page + 1} de ${totalPages}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
      }

      pdf.save(`Costos_AWS_${this.mesReferencia}_vs_${this.mesComparacion}.pdf`);
    } catch (error) {
      console.error('Error al generar PDF:', error);
    } finally {
      this.isExporting = false;
      this.cdr.detectChanges();
    }
  }
}
